
import { Component, Prop, Ref, Vue } from 'vue-property-decorator'
import ManageSidemenuLayout from '@/components/organisms/layout/ManageSidemenuLayout.vue'
import TitlePanel from '@/components/organisms/TitlePanel.vue'
import StudentProgressPanel from '@/components/organisms/manage/StudentProgressPanel.vue'
import { SelectItem } from '@/models/SelectItem'
import { StudentProgress } from '@/models/manage/StudentProgress'

/**
 * 生徒進捗確認画面レイアウト
 */
@Component({
  components: {
    ManageSidemenuLayout,
    TitlePanel,
    StudentProgressPanel,
  },
})
export default class StudentLearningProgressLayout extends Vue {
  /** サイドメニュー部 */
  @Ref()
  private manageSidemenuLayout!: ManageSidemenuLayout

  /** 生徒進捗パネル */
  @Ref()
  studentProgressPanel!: StudentProgressPanel

  /** 先生権限画面の場合true */
  @Prop({ default: false })
  private isTeacher!: boolean

  /** 塾管理者権限画面の場合true */
  @Prop({ default: false })
  private isAcademy!: boolean

  /** 通常学習タブの状態: trueならactive */
  @Prop()
  private isGeneralLearningTabActive?: boolean

  /** 知識MAPタブの状態: trueならactive */
  @Prop()
  private isKnowledgeMapTabActive?: boolean

  /** 生徒情報一覧 */
  @Prop()
  private studentList!: StudentProgress[]

  /** 学年フィルターの選択肢 */
  @Prop()
  private filterGradeList!: SelectItem[]

  /** 学年フィルターの初期選択値 */
  @Prop()
  private defaultGardeCode!: string

  /** 教室フィルターの選択肢 */
  @Prop()
  private filterBranchList?: SelectItem[]

  /** コースフィルターの選択肢 */
  @Prop()
  private filterAcademyCourseList!: SelectItem[]

  /** クラスフィルターの選択肢 */
  @Prop()
  private filterAcademyClassList!: SelectItem[]

  /** クラスフィルターのdisabled制御 */
  @Prop()
  private isClassFilterDisabled!: boolean

  /** 教科フィルターの選択肢 */
  @Prop()
  private filterSubjectList!: SelectItem[]

  /** 教科フィルターの初期選択値 */
  @Prop()
  private defaultSubjectCode!: string

  /** 教材フィルターの選択肢 */
  @Prop()
  private filterProgressUnitList?: SelectItem[]

  /** ページ数 */
  @Prop()
  private pageLength!: number

  /** 現在のページ */
  @Prop()
  private currentPage!: number

  /** 通常学習タブクリック時のイベント */
  @Prop()
  private eventClickGeneralLearningTab?: () => void

  /** 知識MAPタブクリック時のイベント */
  @Prop()
  private eventClickKnowledgeMapTab?: () => void

  /** 学年フィルター変更時のイベント */
  @Prop()
  private changeGradeFilter!: () => Promise<void>

  /** 教室フィルター変更時のイベント */
  @Prop()
  private changeBranchFilter?: () => Promise<void>

  /** コースフィルター変更時のイベント */
  @Prop()
  private changeCourseFilter!: () => Promise<void>

  /** クラスフィルター変更時のイベント */
  @Prop()
  private changeClassFilter!: () => Promise<void>

  /** 生徒検索ボタン押下時のイベント */
  @Prop()
  private eventSearchStudent!: () => Promise<void>

  /** 教科フィルター変更時のイベント */
  @Prop()
  private changeSubjectFilter!: () => Promise<void>

  /** 教材フィルター変更時のイベント */
  @Prop()
  private changeProgressUnitFilter?: () => Promise<void>

  /** 詳細ボタン押下時のイベント */
  @Prop()
  private eventClickDetailButton!: (userId: number) => Promise<void>

  /** ページリンクボタン押下時のイベント */
  @Prop()
  private changePage!: (value: number) => Promise<void>

  /**
   * 選択中の学年
   *
   * @returns 学年コード
   */
  public currentGrade(): string {
    return this.studentProgressPanel.currentGrade()
  }

  /**
   * 選択中の教室
   *
   * @returns 教室コード
   */
  public currentBranch(): string {
    return this.studentProgressPanel.currentBranch()
  }

  /**
   * 教室選択の選択値を設定
   *   選択肢の変更時にselectの値を直接書き換える
   *
   * @param value 選択値指定
   */
  public setCurrentBranchValue(value: string): void {
    this.studentProgressPanel.setCurrentBranchValue(value)
  }

  /**
   * 選択中のコース
   *
   * @returns コースコード
   */
  public currentCourse(): string {
    return this.studentProgressPanel.currentCourse()
  }

  /**
   * コース選択の選択値を設定
   *   選択肢の変更時にselectの値を直接書き換える
   *
   * @param value 選択値指定
   */
  public setCurrentCourseValue(value: string): void {
    this.studentProgressPanel.setCurrentCourseValue(value)
  }

  /**
   * 選択中のクラス
   *
   * @returns クラスコード
   */
  public currentClass(): string {
    return this.studentProgressPanel.currentClass()
  }

  /**
   * クラス選択の選択値を設定
   *   選択肢の変更時にselectの値を直接書き換える
   *
   * @param value 選択値指定
   */
  public setCurrentClassValue(value: string): void {
    this.studentProgressPanel.setCurrentClassValue(value)
  }

  /**
   * 検索欄に入力された文字列
   *
   * @returns 入力された文字列
   */
  public textInputedSearch(): string {
    return this.studentProgressPanel.textInputed()
  }

  /**
   * 検索文字列を設定
   *
   * @param value 指定文字列
   */
  public setCurrentSearchText(value: string): void {
    this.studentProgressPanel.setCurrentSearchText(value)
  }

  /**
   * 選択中の教科
   *
   * @returns 教科コード
   */
  public currentSubject(): string {
    return this.studentProgressPanel.currentSubject()
  }

  /**
   * 選択中の教材
   *
   * @returns 教材コード
   */
  public currentProgressUnit(): string {
    return this.studentProgressPanel.currentProgressUnit()
  }

  /**
   * 教材選択の選択値を設定
   *   選択肢の変更時にselectの値を直接書き換える
   *
   * @param value 選択値指定
   */
  public setCurrentProgressUnitValue(value: string): void {
    this.studentProgressPanel.setCurrentProgressUnitValue(value)
  }
}
